import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, Button, Badge, Card, Row, Col, Space, Spin, message } from 'antd'
import { LeftOutlined, RightOutlined, UnorderedListOutlined, CalendarOutlined, BarChartOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { setViewMode, setCurrentMonth, toggleStatisticsPanel } from '../../store/slices/uiSlice'
import { fetchPlans } from '../../store/slices/planogramPlansSlice'
import { PlanogramPlan } from '../../types/plan'
import StatisticsPanel from './components/StatisticsPanel'
import MilestoneModal from './components/MilestoneModal'
import { useNavigate } from 'react-router-dom'
import './PlanogramPlanCalendar.css'

const PlanogramPlanCalendar: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { plans, loading, filters } = useSelector((state: RootState) => state.planogramPlans)
  const { currentMonth, showStatisticsPanel } = useSelector((state: RootState) => state.ui)
  
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [modalVisible, setModalVisible] = useState(false)
  const [modalDate, setModalDate] = useState<Dayjs | null>(null)
  const [modalPlans, setModalPlans] = useState<PlanogramPlan[]>([])

  useEffect(() => {
    // Only keep the month-based fetching effect
  }, [])

  // Add a new useEffect to fetch plans based on selected month
  useEffect(() => {
    // Create start and end time for the selected month
    const startOfMonth = selectedDate.startOf('month').toDate()
    const endOfMonth = selectedDate.endOf('month').toDate()
    
    // Update filters with the selected month range
    const monthFilters = {
      ...filters,
      startTimeRange: [startOfMonth, endOfMonth] as [Date, Date]
    }
    
    // Fetch plans for the selected month
    dispatch(fetchPlans({
      filters: monthFilters,
      pagination: { currentPage: 1, pageSize: 1000, totalCount: 0 }
    }) as any)
  }, [dispatch, filters, selectedDate])

  // Transform plans for calendar display with milestone information
  const calendarData = useMemo(() => {
    const dataMap = new Map<string, PlanogramPlan[]>()
    
    // Filter plans to only show: Pending, InProgress, Paused (exclude Completed and Cancelled)
    const visiblePlans = plans.filter(plan => 
      plan.status === 'Pending' || plan.status === 'InProgress' || plan.status === 'Paused'
    )
    
    // Group plans by gmtCreate date
    visiblePlans.forEach(plan => {
      // Use gmtCreate date for grouping instead of planned start/end time range
      const createDate = dayjs(plan.gmtCreate)
      const dateKey = createDate.format('YYYY-MM-DD')
      
      if (!dataMap.has(dateKey)) {
        dataMap.set(dateKey, [])
      }
      dataMap.get(dateKey)!.push(plan)
    })
    
    return dataMap
  }, [plans])

  // Calculate milestone statistics for current month
  const milestoneStats = useMemo(() => {
    // Filter plans by gmtCreate date within the current month
    const currentMonthStart = selectedDate.startOf('month')
    const currentMonthEnd = selectedDate.endOf('month')
    
    const monthPlans = plans.filter(plan => {
      const planCreateDate = dayjs(plan.gmtCreate)
      return (
        planCreateDate.isSame(currentMonthStart, 'month') &&
        (plan.status === 'Pending' || plan.status === 'InProgress' || plan.status === 'Paused')
      )
    })

    const milestoneStatusCount = {
      InProgress: 0,
      Completed: 0,
      Pending: 0,
      Paused: 0
    }

    monthPlans.forEach(plan => {
      // Count milestones based on plan status
      // Each plan represents a milestone in the calendar view
      switch (plan.status) {
        case 'InProgress':
          milestoneStatusCount.InProgress++
          break
        case 'Completed':
          milestoneStatusCount.Completed++
          break
        case 'Pending':
          milestoneStatusCount.Pending++
          break
        case 'Paused':
          milestoneStatusCount.Paused++
          break
      }
    })

    return {
      total: monthPlans.length,
      ...milestoneStatusCount,
      plans: monthPlans
    }
  }, [plans, selectedDate])

  // Get status color for plan blocks
  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      'Pending': '#d9d9d9',      // gray
      'InProgress': '#1890ff',   // blue
      'Completed': '#52c41a',    // green
      'Paused': '#fa8c16',       // orange
      'Cancelled': '#ff4d4f'     // red
    }
    return colorMap[status] || '#d9d9d9'
  }

  // Get plan type badge color
  const getPlanTypeBadge = (planType: string): string => {
    const badgeMap: Record<string, string> = {
      'Major': 'red',
      'Regular': 'blue',
      'Minor': 'green',
      'Urgent': 'orange',
      'Special': 'purple'
    }
    return badgeMap[planType] || 'default'
  }

  // Custom date cell renderer - Show milestone details
  const dateCellRender = (value: Dayjs) => {
    const dateKey = value.format('YYYY-MM-DD')
    const dayPlans = calendarData.get(dateKey) || []
    
    if (dayPlans.length === 0) return null

    return (
      <div className="calendar-date-cell-detailed">
        {dayPlans.slice(0, 2).map((plan, index) => (
          <div
            key={`${plan.id}-${index}`}
            className="milestone-item"
            style={{ 
              borderLeft: `3px solid ${getStatusColor(plan.status)}`,
              backgroundColor: `${getStatusColor(plan.status)}15`
            }}
            onClick={(e) => {
              e.stopPropagation()
              handlePlanClick(plan)
            }}
            title={`${plan.planName} - ${plan.currentMilestone}`}
          >
            <div className="milestone-content">
              <div className="milestone-code">
                {plan.planCode}
              </div>
              <div className="milestone-name">
                {plan.currentMilestone}
              </div>
            </div>
          </div>
        ))}
        {dayPlans.length > 2 && (
          <div 
            className="more-milestones"
            onClick={(e) => {
              e.stopPropagation()
              handleDateSelect(value)
            }}
          >
            {t('planManagement.calendar.moreItems', { count: dayPlans.length - 2 })}
          </div>
        )}
      </div>
    )
  }

  // Get status text
  const getStatusText = (status: string): string => {
    const textMap: Record<string, string> = {
      'Pending': t('planManagement.list.status.Pending'),
      'InProgress': t('planManagement.list.status.InProgress'), 
      'Completed': t('planManagement.list.status.Completed'),
      'Paused': t('planManagement.list.status.Paused'),
      'Cancelled': t('planManagement.list.status.Cancelled')
    }
    return textMap[status] || status
  }

  // Handle month change
  const handleMonthChange = (date: Dayjs) => {
    dispatch(setCurrentMonth(date.toDate()))
    setSelectedDate(date)
  }

  // Handle plan click
  const handlePlanClick = (plan: PlanogramPlan) => {
    navigate(`/detail/${plan.id}`)
  }

  // Handle date select - show milestone modal
  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date)
    const dateKey = date.format('YYYY-MM-DD')
    const dayPlans = calendarData.get(dateKey) || []
    
    setModalDate(date)
    setModalPlans(dayPlans)
    setModalVisible(true)
  }

  // Switch to list view
  const switchToListView = () => {
    dispatch(setViewMode('list'))
    navigate('/plans')
  }

  // Toggle statistics panel
  const handleToggleStatistics = () => {
    dispatch(toggleStatisticsPanel())
  }

  // Navigation controls
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = direction === 'prev' 
      ? selectedDate.subtract(1, 'month')
      : selectedDate.add(1, 'month')
    handleMonthChange(newDate)
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Page Header */}
      <div style={{ 
        backgroundColor: '#fff', 
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '24px', fontWeight: 600 }}>
              <CalendarOutlined style={{ marginRight: '8px' }} /> {t('planManagement.calendar.title')}
            </h2>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
              {t('planManagement.calendar.monthlyStats', { month: selectedDate.format('YYYY/MM'), total: milestoneStats.total })}
              <span style={{ marginLeft: '16px' }}>
                {t('planManagement.calendar.statsDetail', { 
                  inProgress: milestoneStats.InProgress,
                  completed: milestoneStats.Completed,
                  pending: milestoneStats.Pending,
                  paused: milestoneStats.Paused
                })}
              </span>
            </div>
          </div>
          
          <div>
            {/* 移除右侧按钮区域 */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px 32px' }}>
        <Spin spinning={loading}>
          <Row gutter={[16, 16]}>
            <Col span={showStatisticsPanel ? 18 : 24}>
              <Card 
                className="calendar-card"
                style={{
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}
              >
                <Calendar
                  value={selectedDate}
                  onSelect={handleDateSelect}
                  onPanelChange={handleMonthChange}
                  cellRender={dateCellRender}
                  headerRender={({ value, onChange }) => (
                    <div className="calendar-custom-header" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 0',
                      borderBottom: '1px solid #f0f0f0',
                      marginBottom: '16px'
                    }}>
                      <div className="calendar-month-year" style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#1f2937'
                      }}>
                        {value.format('YYYY/MM')}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Space>
                          <Button
                            type="default"
                            icon={<UnorderedListOutlined />}
                            onClick={switchToListView}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #d1d5db',
                              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            {t('planManagement.calendar.actions.listView')}
                          </Button>
                          <Button
                            type={showStatisticsPanel ? 'primary' : 'default'}
                            icon={<BarChartOutlined />}
                            onClick={handleToggleStatistics}
                            style={{
                              borderRadius: '8px',
                              backgroundColor: showStatisticsPanel ? '#3b82f6' : undefined,
                              borderColor: showStatisticsPanel ? '#3b82f6' : '#d1d5db',
                              border: '1px solid',
                              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            {t('planManagement.calendar.actions.statisticsPanel')}
                          </Button>
                        </Space>
                        <div style={{ 
                          width: '1px', 
                          height: '24px', 
                          backgroundColor: '#e5e7eb',
                          margin: '0 8px'
                        }} />
                        <Button 
                          icon={<LeftOutlined />}
                          onClick={() => navigateMonth('prev')}
                          style={{
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                        />
                        <Button 
                          icon={<RightOutlined />}
                          onClick={() => navigateMonth('next')}
                          style={{
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                        />
                      </div>
                    </div>
                  )}
                />
              </Card>
            </Col>

            {showStatisticsPanel && (
              <Col span={6}>
                <StatisticsPanel 
                  currentMonth={selectedDate.toDate()}
                  plans={milestoneStats.plans}
                  filters={filters}
                  onClose={() => dispatch(toggleStatisticsPanel())}
                />
              </Col>
            )}
          </Row>
        </Spin>
      </div>

      {/* Milestone Detail Modal */}
      <MilestoneModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        date={modalDate}
        plans={modalPlans}
        onPlanClick={handlePlanClick}
      />
    </div>
  )
}

export default PlanogramPlanCalendar